import { Component, Inject, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccountSettingsDialogService } from '../account-settings-dialog/account-settings-dialog.service';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import { AccountSettingsService } from './account-settings.service';
import {
  ButtonSize, ButtonStyle,
} from '../../aero/aero-button/aero-button.component';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { TranslationService } from '../../common/translation.service';
import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { AnalyticsService } from '../../common/analytics.service';

/**
 * Component for managing parent accounts and family-related settings.
 * Provides functionality to enable/disable family accounts, add students,
 * disconnect parents or students, copy invitation codes, and upgrade account plans.
 */
@Component({
  selector: 'bulb-parent-accounts',
  templateUrl: './parent-accounts.component.html',
  styleUrls: ['./parent-accounts.component.scss'],
  standalone: false,
})
export class ParentAccountsComponent implements OnInit {
  public ButtonSize = ButtonSize;

  public ButtonStyle = ButtonStyle;

  public account: any;

  public loading = false;

  public codeCopied = false;

  /**
   * Creates a new instance of the component with the specified dependencies.
   * @param config - Configuration object for the dynamic dialog
   * @param accountSettingsService - Service for managing account settings
   * @param accountDialogService - Service for handling account-related dialogs
   * @param dialogService - Service for managing dialog operations
   * @param dialogRef - Reference to the dialog instance
   * @param busyService - Service for managing busy states
   * @param authService - Service for authentication operations
   * @param errorService - Service for handling errors
   * @param state - State management service
   * @param translation - Service for translation functionality
   * @param appConfig - Service for application configuration
   * @param dynamicDialogRef - Reference to the dynamic dialog
   * @param analyticsService - Service for analytics tracking
   */
  constructor(
    private config: DynamicDialogConfig,
    private accountSettingsService: AccountSettingsService,
    private accountDialogService: AccountSettingsDialogService,
    private dialogService: AeroDialogService,
    @Inject('DialogRef') private dialogRef: any,
    @Inject(BULB_BUSY_COVER_SERVICE) private busyService: any,
    @Inject('BulbAuthService') private authService: any,
    @Inject('BulbErrorService') private errorService: any,
    @Inject('$state') private state: any,
    private translation: TranslationService,
    private appConfig: AppConfigService,
    private dynamicDialogRef: DynamicDialogRef,
    private analyticsService: AnalyticsService,
  ) {
  }

  /**
   * Initializes the component when it is being initialized.
   * Sets the account property to the value provided in the configuration data.
   *
   * @return {void}
   */
  ngOnInit(): void {
    this.account = this.config.data.account;
  }

  /**
   * Checks if the account represents a student position.
   * @return {boolean} True if the account position is 'STUDENT', false otherwise.
   */
  get isStudent(): boolean {
    return this.account?.position === 'STUDENT';
  }

  /**
   * Checks if the account has the FAMILY_MEMBER role.
   * @return {boolean} True if the account has the FAMILY_MEMBER role, false otherwise.
   */
  get isFamilyMember(): boolean {
    return this.account?.roles?.includes('FAMILY_MEMBER');
  }

  /**
   * Retrieves the enabled status of the student account feature.
   * @return {boolean} True if the student account feature is enabled, false otherwise.
   */
  get studentAccountEnabled(): boolean {
    return !!this.account?.features?.FamilyFeature?.studentAccountEnabled;
  }

  /**
   * Gets the parent accounts linked to this family account.
   * @returns The linked family members, or an empty array when none are available.
   */
  get familyMembers(): any[] {
    return this.account?.family?.familyMembers || [];
  }

  /**
   * Gets the student account identifiers linked to this family account.
   * @returns The linked student identifiers, or an empty array when none are available.
   */
  get students(): any[] {
    return this.account?.family?.studentIds || [];
  }

  /**
   * Indicates whether account upgrades are available through Stripe.
   * @returns True when Stripe is enabled.
   */
  get canUpgrade(): boolean {
    return this.appConfig.stripe?.enabled === true;
  }

  /**
   * Gets the origin of the current page for constructing absolute URLs.
   * @returns The current window location origin.
   */
  get urlOrigin(): string {
    return window.location.origin;
  }

  /** Enables family accounts for the current account. */
  enable(): void {
    this.updateFamily('enable');
  }

  /** Disables family accounts for the current account. */
  disable(): void {
    this.updateFamily('disable');
  }

  /** Opens the add-student dialog and refreshes this dialog with the updated account. */
  addStudent(): void {
    this.closeDialog();
    setTimeout(() => {
      this.accountDialogService.showAddChildDialog(this.account.id, (newStudent) => {
        this.account = newStudent;

        if (this.config.data && this.config.data.accountUpdated) {
          this.config.data.accountUpdated(this.account);
        }
        setTimeout(() => {
          this.config.data.reopenDialog();
        });
      });
    });
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /** Copies the family's invitation code to the clipboard and briefly shows confirmation. */
  copyStudentCode(): void {
    const inviteCode = this.account.family?.inviteCode;
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode).then(() => {
      this.codeCopied = true;
      window.setTimeout(() => {
        this.codeCopied = false;
      }, 7000);
    });
  }

  /**
   * Prompts for confirmation before disconnecting a parent account.
   * @param parent The parent account to disconnect.
   */
  disconnectParent(parent: any): void {
    this.confirmDisconnect('account_settings_disconnect_parent', () => {
      this.updateFamily('removeParent', parent);
    });
  }

  /**
   * Prompts for confirmation before disconnecting a student account.
   * @param student The student account to disconnect.
   */
  disconnectStudent(student: any): void {
    this.confirmDisconnect('account_settings_disconnect_student', () => {
      this.updateFamily('removeChild', student);
    });
  }

  /**
   * Navigates to the specified user's profile page.
   * Prevents the default anchor navigation, closes the currently open dialog,
   * and then redirects the browser to the new URL.
   *
   * @param username - The username of the student whose profile is being viewed.
   * @param event - The DOM click event triggered by the user.
   */
  viewProfile(username: string, event: Event): void {
    event.preventDefault();

    this.closeDialog();

    window.location.href = `${this.urlOrigin}/${username}`;
  }

  /** Tracks student activation and navigates to the plan selection page. */
  upgrade(): void {
    // Fire the Student Activation Event
    this.analyticsService.pushEvent({
      event: 'FamilyAccountActivation',
      activationType: 'studentActivation',
      userId: this.authService.currentUser.id,
    });

    this.dialogRef.close();
    this.state.go('planlist');
  }

  /**
   * Applies a family-account action, synchronizes the account state, and tracks relevant analytics.
   * @param action The family-account operation to perform.
   * @param targetMember The parent or student affected by a disconnect operation, when applicable.
   */
  private updateFamily(action: string, targetMember?: any): void {
    const familyMemberId = targetMember ? targetMember.id : undefined;

    const request = familyMemberId
      ? this.accountSettingsService.disconnectFamilyMember(action as 'removeParent' | 'removeChild', familyMemberId)
      : this.accountSettingsService.toggleFamilyAccounts(action);

    const operation = firstValueFrom(request).then((account: any) => {

      // Update local state based on action
      if (action === 'removeParent' && targetMember) {
        this.account.family.familyMembers = this.account.family.familyMembers
          .filter((member: any) => member.id !== targetMember.id);
      } else {
        this.account = account;
      }

      if (action === 'enable') {
        this.analyticsService.pushEvent({
          event: 'FamilyAccountActivation',
          activationType: 'parentActivation',
          userId: this.authService.currentUser.id,
        });
      } else if (action === 'removeParent' && targetMember) {
        this.analyticsService.pushEvent({
          event: 'FamilyAccountParentDisconnect',
          parentId: targetMember.id,
          parentUsername: targetMember.username,
          userId: this.authService.currentUser.id,
        });
      } else if (action === 'removeChild' && targetMember) {
        this.analyticsService.pushEvent({
          event: 'FamilyAccountStudentDisconnect',
          studentId: targetMember.id,
          studentUsername: targetMember.username,
          userId: this.authService.currentUser.id,
        });
      }

      this.authService.setCurrentUser(this.account);
      this.config.data.accountUpdated(this.account);
      setTimeout(() => {
        this.config.data.reopenDialog();
      });
    }).catch((error) => this.errorService.showError(null, error));

    this.busyService.showBusyDuring(operation);
  }

  /**
   * Closes the current dialog and presents a confirmation dialog for a disconnect action.
   * @param detailCode Translation key for the confirmation details.
   * @param onPrimary Callback invoked when the user confirms the disconnect.
   */
  private confirmDisconnect(detailCode: string, onPrimary: () => void): void {
    this.closeDialog();
    setTimeout(() => {
      this.dialogService.showDialog({
        icon: 'fa fa-exclamation-triangle',
        iconClass: 'aero-alert',
        title: this.translation.translateInstant('modal_default_title'),
        detail: this.translation.translateInstant(detailCode),
        primaryLabel: this.translation.translateInstant('account_settings_disconnect'),
        primaryStyle: ButtonStyle.NEGATIVE_ACTION,
        onPrimary,
      });
    });
  }
}
